import type { VintedSessionRefreshTriggerReason } from "src/interfaces/event";

/**
 * Pure predicates describing an unhealthy Vinted response. Kept outside
 * `vinted.ts` / `VintedClient.ts` so both the integration client and the
 * add-integration flow can share them without an import cycle.
 */

export function isVintedRateLimitedPath(urlOrPath: string) {
  if (!urlOrPath) return false;
  return /\/rate-limited(?:\/|\?|#|$)/i.test(urlOrPath);
}

export function isVintedSessionRefreshPath(urlOrPath: string) {
  if (!urlOrPath) return false;
  return /\/session-refresh(?:\/|\?|#|$)/i.test(urlOrPath);
}

function isInvalidAuthenticationTokenBody(data: unknown): boolean {
  if (!data || typeof data !== "object") return false;
  return (
    (data as { message_code?: unknown }).message_code ===
    "invalid_authentication_token"
  );
}

/**
 * Final URL landed on /rate-limited or /session-refresh, or API said invalid
 * token — recover via wait schedule + session-refresh popup (same retries as 429).
 */
export function needsVintedSessionPopup(res: {
  status: number;
  url?: string;
  data?: unknown;
}): boolean {
  if (isVintedRateLimitedPath(res.url || "")) return true;
  if (isVintedSessionRefreshPath(res.url || "")) return true;
  if (res.status === 401 && isInvalidAuthenticationTokenBody(res.data)) {
    return true;
  }
  return false;
}

export function vintedSessionPopupReason(res: {
  status: number;
  url?: string;
  data?: unknown;
}): VintedSessionRefreshTriggerReason {
  if (isVintedRateLimitedPath(res.url || "")) {
    return "rate-limited";
  }
  if (isVintedSessionRefreshPath(res.url || "")) {
    return "homepage-session-refresh";
  }
  return "api-invalid-authentication-token";
}
